import React from 'react';
import { Link } from 'react-router-dom';
import { FileCheck, Clock, Camera, Phone, CheckCircle2, TrendingUp } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';

export const LettingAgents = () => {
  useScrollAnimations();
  const serviceJourney = [
    {
      icon: <Phone size={28} />,
      title: 'Job Raised',
      description: 'You send us the job details. We handle everything from there.'
    },
    {
      icon: <CheckCircle2 size={28} />,
      title: 'Booking Confirmed',
      description: 'Immediate confirmation with appointment details and ETA.'
    },
    {
      icon: <Clock size={28} />,
      title: 'Updates & Communication',
      description: 'On-the-way updates and direct tenant liaison if needed.'
    },
    {
      icon: <Camera size={28} />,
      title: 'First Visit Assessment',
      description: 'Full diagnosis with photos and clear recommendations.'
    },
    {
      icon: <FileCheck size={28} />,
      title: 'Work Completion',
      description: 'Professional completion report with before/after photos.'
    },
    {
      icon: <TrendingUp size={28} />,
      title: 'Invoice & Follow-Up',
      description: 'Clear invoicing and ongoing support for any follow-ups.'
    }
  ];

  const benefits = [
    {
      title: 'Reduced Admin Burden',
      description: 'Hand the job over completely. We manage communication, scheduling, and tenant liaison.'
    },
    {
      title: 'Professional Reporting',
      description: 'Detailed reports with photos, work completed, and recommendations for future maintenance.'
    },
    {
      title: 'Consistent Quality',
      description: 'Repair-first approach with guaranteed workmanship. Build trust with your landlords.'
    },
    {
      title: 'Reliable Response Times',
      description: 'Emergency call-out available. Consistent follow-through on commitments.'
    },
    {
      title: 'Transparent Pricing',
      description: 'Clear quotes before work proceeds. No hidden costs or surprise invoices.'
    },
    {
      title: 'Long-Term Partnership',
      description: 'We understand your business. Build a reliable service relationship that lasts.'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(200, 168, 96, 0.2)',
            borderRadius: '6px',
            color: 'var(--royal-gold)',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '20px',
            border: '1px solid rgba(200, 168, 96, 0.3)'
          }}>
            For Property Professionals
          </div>
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Letting Agents & Property Managers
          </h1>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: '600', 
            color: 'var(--royal-gold)', 
            marginBottom: '16px',
            fontStyle: 'italic'
          }}>
            Convenience as a Service
          </h2>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Reduce admin, improve tenant satisfaction, and build trust with landlords through reliable, professional service. Hand over the hassle – we handle everything.
          </p>
        </div>
      </section>

      {/* Service Journey */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">The Service Journey</h2>
            <p className="royal-body-lg">
              From job raised to invoice, we handle everything. Here's how it works.
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {serviceJourney.map((step, index) => (
              <div key={index} style={{
                padding: '32px',
                background: 'var(--bg-section-alt)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-12px',
                  left: '32px',
                  background: 'var(--royal-gold)',
                  color: 'var(--royal-navy)',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '0.9rem'
                }}>
                  {index + 1}
                </div>
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: 'var(--royal-navy)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '20px',
                  marginTop: '8px',
                  color: 'var(--royal-gold)'
                }}>
                  {step.icon}
                </div>
                <h3 style={{
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'var(--royal-navy)'
                }}>
                  {step.title}
                </h3>
                <p className="royal-body">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          <div style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}>
            <h2 className="royal-heading-lg">Why Agents Choose Royal Security</h2>
            <p className="royal-body-lg">
              Built for long-term partnerships, not one-off jobs.
            </p>
          </div>

          <div className="royal-grid royal-grid-3">
            {benefits.map((benefit, index) => (
              <div key={index} style={{
                padding: '32px',
                background: 'var(--royal-white)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--royal-gold)';
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'var(--border-color)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{
                  width: '8px',
                  height: '40px',
                  background: 'var(--royal-gold)',
                  borderRadius: '4px',
                  marginBottom: '20px'
                }}></div>
                <h3 style={{
                  fontSize: '1.15rem',
                  fontWeight: '600',
                  marginBottom: '12px',
                  color: 'var(--royal-navy)'
                }}>
                  {benefit.title}
                </h3>
                <p className="royal-body">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documentation & Reporting */}
      <section className="royal-section" style={{ background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '48px',
            alignItems: 'center'
          }}>
            <div>
              <h2 className="royal-heading-md">Professional Documentation</h2>
              <p className="royal-body-lg" style={{ marginBottom: '24px' }}>
                Every job includes professional reporting with photos, work completed, and recommendations.
              </p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {[
                  'Before and after photos',
                  'Detailed work descriptions',
                  'Parts used and warranty info',
                  'Future maintenance recommendations',
                  'Clear, itemized invoicing'
                ].map((item, index) => (
                  <li key={index} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '12px'
                  }}>
                    <CheckCircle2 size={20} style={{ color: 'var(--royal-gold)', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.95rem' }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{
              background: 'var(--royal-navy)',
              padding: '40px',
              borderRadius: '12px',
              color: 'var(--royal-white)'
            }}>
              <Camera size={48} style={{ color: 'var(--royal-gold)', marginBottom: '24px' }} />
              <h3 style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px', color: 'var(--royal-white)' }}>
                Documentation You Can Trust
              </h3>
              <p style={{ fontSize: '1rem', lineHeight: '1.7', marginBottom: '24px' }}>
                Build confidence with landlords and tenants through transparent, professional reporting on every job.
              </p>
              <div style={{
                padding: '20px',
                background: 'rgba(200, 168, 96, 0.15)',
                borderRadius: '8px',
                border: '1px solid rgba(200, 168, 96, 0.3)'
              }}>
                <p style={{ fontSize: '0.95rem', lineHeight: '1.6', margin: 0, fontStyle: 'italic' }}>
                  "Clear evidence of work completed reduces disputes and builds trust with all parties."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'var(--royal-white)'
          }}>
            Ready to Build a Partnership?
          </h2>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 32px'
          }}>
            Let's discuss how Royal Security can support your agency with reliable, professional service.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Get in Touch
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
